import React from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { AstralInfo } from '../../hooks/useAstralInfo';

// Reflexões por elemento
const REFLECTIONS: Record<string, string[]> = {
  Fogo: [
    '"A paixão que você carrega é a faísca que transforma o mundo ao seu redor."',
    '"Sua energia é um presente — use-a para iluminar, não para consumir."',
    '"O coragem não é ausência de medo, é agir apesar dele."',
    '"Cada chama começa com uma única centelha de intenção."',
    '"Liderar com o coração abre portas que a força sozinha não consegue."',
  ],
  Terra: [
    '"O que você constrói com paciência dura muito mais do que o que foi feito às pressas."',
    '"Enraize-se no presente — é daqui que toda transformação genuína parte."',
    '"Sua estabilidade é um presente para todos que estão ao seu redor."',
    '"A perseverança silenciosa move montanhas que o barulho não consegue."',
    '"Cuide da sua base e ela cuidará de tudo que você construir sobre ela."',
  ],
  Ar: [
    '"Uma mente aberta é o solo mais fértil para novas possibilidades."',
    '"Suas palavras têm o poder de curar — escolha-as com sabedoria e amor."',
    '"A comunicação autêntica conecta almas que a distância não consegue separar."',
    '"Ideias são sementes; ação é a chuva que as faz florescer."',
    '"Questione tudo com curiosidade, não com julgamento."',
  ],
  Água: [
    '"Sua sensibilidade é uma força, não uma fraqueza."',
    '"Como a água, você tem o poder de contornar obstáculos e encontrar o caminho."',
    '"A empatia que você oferece aos outros, ofereça também a si mesmo."',
    '"As emoções são mensagens da alma — escute-as com gentileza."',
    '"Fluir não é desistir — é confiar na direção que a vida te convida a seguir."',
  ],
};

// Práticas por elemento
const PRACTICES: Record<string, string[]> = {
  Fogo: [
    'Faça 10 minutos de exercício físico intenso para liberar a energia do dia.',
    'Escreva 3 metas concretas que deseja conquistar esta semana.',
    'Dance por 5 minutos com uma música que te energize — sem julgamentos.',
    'Acenda uma vela e visualize um objetivo importante se realizando.',
    'Faça algo criativo hoje: desenhe, cozinhe algo novo, improvise.',
  ],
  Terra: [
    'Caminhe descalço(a) na grama ou terra por alguns minutos — reconecte-se.',
    'Organize um espaço físico da sua casa ou trabalho que esteja bagunçado.',
    'Prepare uma refeição nutritiva com atenção plena a cada ingrediente.',
    'Plante uma semente — seja no jardim, num vaso, ou na imaginação.',
    'Faça uma lista de tudo que você já conquistou. Releia com gratidão.',
  ],
  Ar: [
    'Pratique respiração profunda: 4 tempos inspirando, 4 segurando, 4 soltando.',
    'Leia 10 páginas de um livro que expanda sua visão de mundo.',
    'Escreva num diário por 5 minutos sem parar — deixe os pensamentos fluírem.',
    'Medite observando apenas o movimento do ar entrando e saindo.',
    'Tenha uma conversa verdadeira com alguém — escute mais do que fale.',
  ],
  Água: [
    'Tome um banho com intenção: visualize preocupações indo embora com a água.',
    'Pratique 10 minutos de meditação focando nas emoções presentes, sem julgá-las.',
    'Escreva uma carta de perdão — para si mesmo(a) ou para alguém.',
    'Ouça uma música que te toque profundamente e permita-se sentir.',
    'Hidrate-se conscientemente durante o dia — cada gole é um ato de autocuidado.',
  ],
};

// Afirmações por número do Caminho de Vida
const AFFIRMATIONS: Record<number, string[]> = {
  1: ['"Sou pioneiro(a) da minha própria história."', '"Minha liderança vem do amor e da autenticidade."', '"Confio na minha capacidade de criar novos começos."'],
  2: ['"Minha sensibilidade é uma força poderosa."', '"Crio harmonia em tudo que toco."', '"Mereço dar e receber amor em equilíbrio."'],
  3: ['"Me expresso com alegria e criatividade."', '"Minha voz tem valor e merece ser ouvida."', '"Cada dia é uma tela em branco para minha expressão."'],
  4: ['"Construo uma vida sólida, passo a passo."', '"Minha disciplina me leva onde meu coração deseja."', '"Sou confiável, para mim e para quem amo."'],
  5: ['"Abraço a mudança com coragem e curiosidade."', '"A liberdade é o estado natural da minha alma."', '"Cada experiência me expande e me transforma."'],
  6: ['"Cuido de mim para poder cuidar de quem amo."', '"Sou um porto seguro para minha família e amigos."', '"O amor que ofereço volta a mim multiplicado."'],
  7: ['"Confio no silêncio — ele me traz respostas."', '"Minha espiritualidade é minha âncora mais profunda."', '"Busco o conhecimento que liberta, não que aprisiona."'],
  8: ['"Mereço prosperidade e uso meu poder com sabedoria."', '"Cada desafio é um degrau para minha realização."', '"Abundância flui naturalmente na minha vida."'],
  9: ['"Sirvo ao mundo com amor e sem perder a mim mesmo(a)."', '"Minha compaixão é ilimitada, começando por mim."', '"Completo ciclos com gratidão e abro espaço ao novo."'],
  11: ['"Sou um canal de luz e inspiração."', '"Minha intuição me guia com precisão divina."', '"Elevo a consciência ao meu redor apenas sendo eu mesmo(a)."'],
  22: ['"Construo sonhos que transformam realidades."', '"Minha visão serve à humanidade, não ao ego."', '"Tenho o poder de manifestar o extraordinário."'],
  33: ['"Curar e inspirar é minha missão sagrada."', '"Ofereço amor incondicional começando por mim."', '"Sou um instrumento de cura e transformação."'],
};

interface Props {
  astralInfo: AstralInfo | null;
}

const CrescimentoPessoal: React.FC<Props> = ({ astralInfo }) => {
  const { user } = useAuth();

  // Semente determinística: muda por dia + por usuário
  const getDaySeed = (extra = 0) => {
    const d = new Date();
    const dateVal = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    const userId = user?.id || 0;
    return (dateVal * 31 + userId * 17 + extra * 7) >>> 0;
  };
  const pickFrom = <T,>(arr: T[], seed: number): T => arr[seed % arr.length];

  const element = astralInfo?.element || 'Fogo';
  const elementKey = element === 'Fogo' ? 'Fogo' : element === 'Terra' ? 'Terra' : element === 'Ar' ? 'Ar' : 'Água';
  const lifePathNum = astralInfo?.numerologyLabels ? astralInfo.luckyNumbers[0] : 9;
  const affirmPool = AFFIRMATIONS[lifePathNum] || AFFIRMATIONS[9];

  const reflection = pickFrom(REFLECTIONS[elementKey] || REFLECTIONS['Fogo'], getDaySeed(1));
  const practice = pickFrom(PRACTICES[elementKey] || PRACTICES['Fogo'], getDaySeed(2));
  const affirmation = pickFrom(affirmPool, getDaySeed(3));

  return (
    <div className="backdrop-blur-sm rounded-xl p-6 border border-white/20" style={{ background: 'rgba(255,255,255,0.05)' }}>
      <div className="flex items-center space-x-2 mb-4">
        <HeartIcon className="w-6 h-6 text-pink-400" />
        <h3 className="text-lg font-semibold text-white">Crescimento Pessoal</h3>
      </div>

      <div className="space-y-3">
        <div className="p-3 bg-pink-400/10 rounded-lg border border-pink-400/10">
          <h4 className="text-sm font-semibold text-pink-400 mb-1">
            💝 Reflexão para {element || 'Você'}
          </h4>
          <p className="text-xs text-gray-300 leading-relaxed italic">{reflection}</p>
        </div>

        <div className="p-3 bg-blue-400/10 rounded-lg border border-blue-400/10">
          <h4 className="text-sm font-semibold text-blue-400 mb-1">
            🧘 Prática do Elemento {element}
          </h4>
          <p className="text-xs text-gray-300 leading-relaxed">{practice}</p>
        </div>

        <div className="p-3 bg-green-400/10 rounded-lg border border-green-400/10">
          <h4 className="text-sm font-semibold text-green-400 mb-1">
            🌱 Afirmação{lifePathNum ? ` — Caminho de Vida ${lifePathNum}` : ''}
          </h4>
          <p className="text-xs text-gray-300 leading-relaxed italic">{affirmation}</p>
        </div>
      </div>
    </div>
  );
};

export default CrescimentoPessoal;
